import React from "react";
import {
  auditActionLabel,
  auditActorDisplay,
  formatAuditTimestamp,
} from "../../utils/auditDisplay";

/** @typedef {{ label: string, old: string, new: string }} AuditChange */

const metaMuted =
  "text-[11px] text-slate-600 dark:text-[#6b7a9a] leading-snug";

const summaryText =
  "text-sm font-semibold text-[#0f172a] dark:text-[#eef2ff] leading-snug";

function displayCell(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function isNarrativeOnlyChange(c) {
  const o = displayCell(c.old);
  return (c.label === "Ereignis" || c.label === "Details") && (o === "—" || o === "");
}

/**
 * Structured old/new rows for audit field diffs (presentation only).
 * @param {{ changes: AuditChange[], className?: string, listClassName?: string }} props
 */
export function AuditChangeRows({ changes, className = "", listClassName = "" }) {
  if (!changes || changes.length === 0) return null;
  return (
    <ul className={`space-y-2.5 ${listClassName || "mt-2.5"}`}>
      {changes.map((c, idx) => {
        if (isNarrativeOnlyChange(c)) {
          return (
            <li
              key={idx}
              className="rounded-lg border border-slate-200/50 bg-slate-50/70 px-2.5 py-2.5 text-[12px] text-[#0f172a] shadow-sm dark:border-white/[0.06] dark:bg-[#0c1018]/70 dark:text-[#e2e8f0]"
            >
              {displayCell(c.new)}
            </li>
          );
        }
        return (
          <li
            key={idx}
            className={`rounded-lg border border-slate-200/50 bg-white/90 px-2.5 py-2.5 shadow-sm dark:border-white/[0.06] dark:bg-[#111520]/80 ${className}`}
          >
            <div className="text-[11px] font-medium text-slate-700 dark:text-[#94a3b8]">{c.label}</div>
            <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2 sm:gap-3">
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-[#64748b]">
                  Alt
                </div>
                <div className="mt-0.5 break-words rounded-md border border-amber-200/80 bg-amber-50/90 px-2 py-1.5 text-[12px] text-amber-950 tabular-nums dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-100">
                  {displayCell(c.old)}
                </div>
              </div>
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-[#64748b]">
                  Neu
                </div>
                <div className="mt-0.5 break-words rounded-md border border-emerald-200/80 bg-emerald-50/90 px-2 py-1.5 text-[12px] text-emerald-950 tabular-nums dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-100">
                  {displayCell(c.new)}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One audit entry for admin entity timelines (summary → changes → meta).
 * @param {object} props
 * @param {string} props.summary
 * @param {AuditChange[]} [props.changes]
 * @param {string} [props.createdAt]
 * @param {string|null} [props.actor]
 * @param {string} [props.action] — raw action; shown via auditActionLabel in meta when showActionBadge is true
 * @param {boolean} [props.showActionInMeta]
 * @param {string} [props.dotClassName]
 * @param {string} [props.metaClassName]
 * @param {React.ComponentType<{className?: string}>} [props.icon] — OPT-IN.
 *   When given, the plain dot becomes a small tinted tile carrying this icon.
 *   Omit it and the rendered DOM is byte-identical to before: six pages other
 *   than Inventar render this component and none of them passes an icon.
 * @param {string} [props.iconTileClassName]
 */
export function AdminAuditTimelineEntry({
  summary,
  changes = [],
  createdAt,
  actor,
  action,
  showActionInMeta = true,
  dotClassName = "bg-[#fb923c]",
  metaClassName,
  icon: Icon = null,
  iconTileClassName = "border-border bg-muted text-muted-foreground",
}) {
  const act = actor != null ? actor : null;
  const metaCls = metaClassName || metaMuted;
  const parts = [];
  if (createdAt) parts.push(formatAuditTimestamp(createdAt));
  if (showActionInMeta && action != null && String(action) !== "") {
    parts.push(auditActionLabel(action));
  }
  if (act) parts.push(act);
  const metaLine = parts.join(" · ");

  return (
    <li className={Icon ? "relative pl-4" : "relative pl-2"}>
      {Icon ? (
        <span
          className={`absolute left-[-14px] top-0 flex h-6 w-6 items-center justify-center rounded-full border ${iconTileClassName}`}
          aria-hidden
        >
          <Icon className="h-3 w-3" />
        </span>
      ) : (
        <span
          className={`absolute left-[-5px] top-1.5 h-2 w-2 rounded-full ${dotClassName}`}
          aria-hidden
        />
      )}
      <p className={`${summaryText} mb-0`}>
        {summary}
      </p>
      <AuditChangeRows changes={changes} />
      {metaLine ? <p className={`mt-2.5 ${metaCls}`}>{metaLine}</p> : null}
    </li>
  );
}

/**
 * Timeline wrapper matching AdminUnitDetailPage structure (border-l + spacing).
 */
export function AdminAuditTimeline({ children, className = "" }) {
  return (
    <ul
      className={`ml-0 list-none space-y-5 border-l border-slate-200/55 pl-3.5 dark:border-white/[0.06] ${className}`}
    >
      {children}
    </ul>
  );
}

export { auditActorDisplay, formatAuditTimestamp, auditActionLabel };
